import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore, type ToolItem } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_NUM_CLUSTERS, INPUT_NAMES } from '@/tools/clusteringV2/clustering.constants';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolParameter,
} from '@/toolSelection/baseTool.store';
import type { Capsule } from '@/utilities/datetime.constants';

interface ClusteringState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // monkey() computed value
  modelPropertiesExpanded: boolean;
  numClusters: number;
  inputSignals?: ToolItem[];
  condition?: ToolItem;
  restrictToCondition?: ToolItem;
}

export type ClusteringDehydratedState = Omit<ClusteringState, 'window'>;

export class ClusteringStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqClusteringStoreV2';
  type = TREND_TOOLS.CLUSTERING_V_2;

  parameterDefinitions = {
    inputSignals: {
      predicate: (parameter: ToolParameter) => parameter?.name?.startsWith(INPUT_NAMES.signalInput),
      multiple: true,
    },
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
  };

  initialize() {
    const initialState: ClusteringState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      numClusters: DEFAULT_NUM_CLUSTERS,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numClusters(): number {
    return this.state.get('numClusters');
  }

  dehydrate(): ClusteringDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<ClusteringDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setNumClusters(payload: { numClusters: number }): void {
    this.state.set('numClusters', payload.numClusters);
  }

  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  modelToolRehydrateForEdit = (payload: ModelRehydratePayload): void => {
    if (payload.type !== this.type) {
      return;
    }
    this.initialize();
    this.state.set('id', payload.instanceId);
    (payload.modelData?.inputFeatures ?? []).forEach((feature: any): void => {
      const inputName: string = feature.name;
      switch (inputName) {
        case SeeqNames.Models.InstanceNameInputFeatureName:
          // TODO: CRAB-53864: Handle duplicate names more gracefully
          this.state.set('name', payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`);
          break;
        case INPUT_NAMES.numClustersInput:
          this.state.set('numClusters', feature.inputNumeric ?? DEFAULT_NUM_CLUSTERS);
          break;

        case INPUT_NAMES.startTimeInput: {
          const startTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowStart', moment(startTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.endTimeInput: {
          const endTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowEnd', moment(endTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.trainWithinConditionInput:
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('condition', _.pick(item, ['id', 'name']));
          }
          break;

        case INPUT_NAMES.restrictOutputToConditionInput: {
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('restrictToCondition', _.pick(item, ['id', 'name']));
          }
          break;
        }
        default:
          if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
            const existingSignals = this.state.get('inputSignals') || [];
            const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('inputSignals', [...existingSignals, _.pick(item, ['id', 'name'])]);
          }
          break;
      }
    });
  };

  protected readonly handlers = {
    ...this.baseHandlers,
    CLUSTERING_SET_TRAINING_WINDOW: this.setTrainingWindow,
    CLUSTERING_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    CLUSTERING_SET_NUM_CLUSTERS: this.setNumClusters,
    MODEL_TOOL_REHYDRATE_FOR_EDIT: this.modelToolRehydrateForEdit,
  };
}
